import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

/**
 * The response interceptor below unwraps `response.data`, so every request
 * resolves to the payload rather than the full AxiosResponse. Retype the
 * instance to reflect that — otherwise consumers see `Promise<AxiosResponse>`
 * and hit TS2339 (`.map does not exist on AxiosResponse`) on real data.
 * Per-call type params (`axios.get<Foo>(...)`) now flow the DTO to callers.
 */
export interface UnwrappedAxiosInstance
  extends Omit<AxiosInstance, "get" | "post" | "put" | "patch" | "delete" | "head" | "options" | "request"> {
  request<T = any>(config: AxiosRequestConfig): Promise<T>;
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
  head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  put<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  patch<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

const handleResponseSuccess = (response) => {
  return response.data;
};

const handleResponseError = (error) => {
  if (error.response) {
    // server responded with a non 2xx code
    return Promise.reject(error.response.data);
  } else if (error.request) {
    // request was made but no response received
    return Promise.reject(error);
  } else {
    // something else happened before making the request
    return Promise.reject(error);
  }
};

const instance = axios.create() as unknown as UnwrappedAxiosInstance;

instance.interceptors.response.use(handleResponseSuccess, handleResponseError);

export const setAuthHeader = (accessToken) => {
  instance.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
};

export default instance;
